import { Capacitor } from '@capacitor/core';

export type Platform = 'web' | 'ios' | 'android';

export function currentPlatform(): Platform {
  try {
    const p = Capacitor.getPlatform();
    return p === 'ios' || p === 'android' ? p : 'web';
  } catch {
    return 'web';
  }
}

export const STREAK_MILESTONES = [3, 7, 14, 30, 60, 90, 180, 365];

/** Returns the milestone just reached, or null. */
export function streakMilestone(previous: number, next: number): number | null {
  if (next <= previous) return null;
  const hit = STREAK_MILESTONES.filter((m) => m > previous && m <= next);
  return hit.length ? hit[hit.length - 1] : null;
}

/** DAU if last visit was yesterday (local day diff 1), WAU otherwise, null if same day. */
export function sessionWindow(lastVisit: Date, now: Date): 'DAU' | 'WAU' | null {
  const day = (d: Date) => Math.floor(new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() / 86400000);
  const diff = day(now) - day(lastVisit);
  if (diff <= 0) return null;
  return diff === 1 ? 'DAU' : 'WAU';
}

export function hoursSince(iso: string | null | undefined, now = new Date()): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.round(((now.getTime() - t) / 3600000) * 10) / 10);
}

const LAST_VISIT_KEY = 'tva_last_visit_at';
const SESSION_START_KEY = 'tva_session_started_at';

/** Call on app open; returns props for session_returned when it should fire. */
export function noteSessionStart(now = new Date()): { window: 'DAU' | 'WAU'; previous_session_minutes: number | null } | null {
  try {
    const last = localStorage.getItem(LAST_VISIT_KEY);
    const start = localStorage.getItem(SESSION_START_KEY);
    localStorage.setItem(LAST_VISIT_KEY, now.toISOString());
    if (!last) { localStorage.setItem(SESSION_START_KEY, now.toISOString()); return null; }
    const w = sessionWindow(new Date(last), now);
    if (!w) return null;
    localStorage.setItem(SESSION_START_KEY, now.toISOString());
    const mins = start ? Math.round((new Date(last).getTime() - new Date(start).getTime()) / 60000) : null;
    return { window: w, previous_session_minutes: mins !== null && mins >= 0 ? mins : null };
  } catch {
    return null;
  }
}

export function touchLastVisit(now = new Date()): void {
  try { localStorage.setItem(LAST_VISIT_KEY, now.toISOString()); } catch { /* ignore */ }
}
